import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, SectionTag } from "./Reveal";
import { PARTNER_CATEGORIES, PROSPECTIVE_ORGS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { organisation: "", contact_person: "", email: "", category: "", contribution: "", message: "" };

const inputCls =
  "w-full bg-abyss/70 border border-polar/20 text-ice placeholder:text-tech/70 px-4 py-3 text-sm focus:outline-none focus:border-polar transition-colors duration-300";

export default function Partnerships() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.organisation.trim().length < 2) errs.organisation = "Organisation is required";
    if (form.contact_person.trim().length < 2) errs.contact_person = "Contact person is required";
    if (!EMAIL_RE.test(form.email)) errs.email = "A valid professional email is required";
    if (!form.category) errs.category = "Select a partnership category";
    if (form.contribution.trim().length < 2) errs.contribution = "Proposed contribution is required";
    if (form.message.trim().length < 10) errs.message = "Message must be at least 10 characters";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries/partnership`, form);
      toast.success("Partnership enquiry received. The Arjuna team will review it.");
      setForm(EMPTY);
      setErrors({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="partners" data-testid="partnerships-section" className="relative py-24 lg:py-32 bg-abyss/40">
      <div className="absolute inset-0 grid-overlay opacity-40 pointer-events-none" />
      <div className="relative max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <SectionTag index="07" label="Partnerships & Sponsorship" />
          <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-ice leading-[0.95] max-w-3xl">
            Help build <span className="text-polar">the mission</span>
          </h2>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-ice/70 leading-relaxed">
            The programme is seeking appropriate research, technology, logistics and sponsorship partners.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-14">
          <div className="lg:col-span-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {PARTNER_CATEGORIES.map((c, i) => (
                <Reveal key={c} delay={0.06 * i}>
                  <div className="border border-polar/15 bg-[#0B1F35]/70 p-5 h-full hover:border-polar/40 transition-colors duration-300" data-testid={`partner-category-${i}`}>
                    <span className="font-mono text-[10px] text-mission tracking-[0.3em]">0{i + 1}</span>
                    <p className="font-display font-semibold text-lg text-ice mt-2 leading-tight tracking-wide">{c}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.15}>
              <div className="mt-10 border border-gold/25 bg-gold/5 p-6" data-testid="prospective-orgs">
                <div className="font-mono text-[10px] tracking-[0.25em] text-gold mb-4">
                  PROSPECTIVE ORGANISATIONS TO INVESTIGATE — NOT PARTNERS
                </div>
                <ul className="space-y-2">
                  {PROSPECTIVE_ORGS.map((o) => (
                    <li key={o} className="text-sm text-ice/70 flex gap-3">
                      <span className="text-gold shrink-0">·</span>
                      {o}
                    </li>
                  ))}
                </ul>
                <p className="mt-5 text-xs font-mono text-tech leading-relaxed tracking-wide">
                  No logos shown. No endorsement implied. No funding or collaboration has been secured with any of
                  these organisations.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal className="lg:col-span-6" delay={0.1}>
            <form onSubmit={submit} noValidate className="border border-polar/20 bg-[#0B1F35]/80 backdrop-blur-md p-7 sm:p-9" data-testid="partnership-form">
              <div className="font-mono text-[10px] tracking-[0.3em] text-polar mb-7">PARTNERSHIP ENQUIRY — SECURE SUBMISSION</div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="pf-org" className="block font-mono text-[10px] tracking-[0.2em] text-tech mb-2">ORGANISATION *</label>
                  <input id="pf-org" data-testid="partnership-input-organisation" className={inputCls} value={form.organisation} onChange={set("organisation")} placeholder="Institution or company" />
                  {errors.organisation && <p className="text-mission text-xs mt-1.5" data-testid="partnership-error-organisation">{errors.organisation}</p>}
                </div>
                <div>
                  <label htmlFor="pf-contact" className="block font-mono text-[10px] tracking-[0.2em] text-tech mb-2">CONTACT PERSON *</label>
                  <input id="pf-contact" data-testid="partnership-input-contact" className={inputCls} value={form.contact_person} onChange={set("contact_person")} placeholder="Full name" />
                  {errors.contact_person && <p className="text-mission text-xs mt-1.5" data-testid="partnership-error-contact">{errors.contact_person}</p>}
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="pf-email" className="block font-mono text-[10px] tracking-[0.2em] text-tech mb-2">PROFESSIONAL EMAIL *</label>
                <input id="pf-email" type="email" data-testid="partnership-input-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="name@organisation.org" />
                {errors.email && <p className="text-mission text-xs mt-1.5" data-testid="partnership-error-email">{errors.email}</p>}
              </div>

              <div className="mt-5">
                <label htmlFor="pf-category" className="block font-mono text-[10px] tracking-[0.2em] text-tech mb-2">PARTNERSHIP CATEGORY *</label>
                <select id="pf-category" data-testid="partnership-input-category" className={`${inputCls} appearance-none`} value={form.category} onChange={set("category")}>
                  <option value="" disabled>Select a category</option>
                  {PARTNER_CATEGORIES.map((c) => (
                    <option key={c} value={c} className="bg-abyss">{c}</option>
                  ))}
                </select>
                {errors.category && <p className="text-mission text-xs mt-1.5" data-testid="partnership-error-category">{errors.category}</p>}
              </div>

              <div className="mt-5">
                <label htmlFor="pf-contribution" className="block font-mono text-[10px] tracking-[0.2em] text-tech mb-2">PROPOSED CONTRIBUTION *</label>
                <input id="pf-contribution" data-testid="partnership-input-contribution" className={inputCls} value={form.contribution} onChange={set("contribution")} placeholder="e.g. data access, logistics, sponsorship, research collaboration" />
                {errors.contribution && <p className="text-mission text-xs mt-1.5" data-testid="partnership-error-contribution">{errors.contribution}</p>}
              </div>

              <div className="mt-5">
                <label htmlFor="pf-message" className="block font-mono text-[10px] tracking-[0.2em] text-tech mb-2">MESSAGE *</label>
                <textarea id="pf-message" rows={4} data-testid="partnership-input-message" className={`${inputCls} resize-y`} value={form.message} onChange={set("message")} placeholder="Tell us how you'd like to be involved" />
                {errors.message && <p className="text-mission text-xs mt-1.5" data-testid="partnership-error-message">{errors.message}</p>}
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="partnership-submit-button"
                className="mt-8 w-full bg-mission text-ice font-mono text-xs uppercase tracking-[0.25em] py-4 clip-corner hover:bg-ice hover:text-midnight transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? "Transmitting…" : "Discuss a Partnership"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
